/**
 * Enterprise Policy Engine
 *
 * Centralized access control evaluation engine that processes
 * RBAC, ABAC, and policy-based access decisions.
 */
import { match } from 'path-to-regexp';
// import { logger } from "../utils/logger";
import { AccessPolicy, AccessPolicyRegistry, AccessEvaluationResult, AccessCondition, UserAttributes, Role, Permission, AccessMonitoringEvent, PolicyConfiguration } from '../types/accessControl';
import { defaultAccessPolicyRegistry } from '../config/accessPolicies';
import AuditService from './AuditService';
import { logger } from "../utils/logger";
export class PolicyEngine {
    private static registry: AccessPolicyRegistry = defaultAccessPolicyRegistry;
    private static auditService: AuditService = new AuditService();
    private static config: PolicyConfiguration = {
        enableABAC: true,
        enableMonitoring: true,
        cache: {
            enabled: true,
            ttl: 300000 // 5 minutes
        },
        debug: {
            enabled: import.meta.env.MODE === 'development',
            logLevel: 'info'
        }
    };
    // Cache for route matching patterns
    private static routeMatchers = new Map<string, any>();
    /**
     * Initialize the policy engine with custom configuration
     */
    static initialize(config: Partial<PolicyConfiguration> = {}): void {
        this.config = { ...this.config, ...config };
        if (this.config.debug?.enabled) {
            logger.debug('[PolicyEngine] Initialized with config:', { "arg0": this.config });
        }
    }
    /**
     * Update the policy registry (for dynamic policy loading)
     */
    static updateRegistry(registry: AccessPolicyRegistry): void {
        this.registry = registry;
        this.routeMatchers.clear(); // Clear cache when registry changes
        if (this.config.debug?.enabled) {
            logger.debug('[PolicyEngine] Registry updated');
        }
    }
    /**
     * Evaluate access for a user and route
     */
    static evaluate(user: UserAttributes | null, route: string, resource?: string, correlationId?: string): AccessEvaluationResult {
        const startTime = performance.now();
        const timestamp = new Date().toISOString();
        // Generate correlation ID if not provided
        const evalCorrelationId = correlationId || this.generateCorrelationId();
        try {
            // Handle unauthenticated users
            if (!user) {
                return this.createEvaluationResult(false, 'User not authenticated', [], [], user, route, timestamp, performance.now() - startTime, evalCorrelationId);
            }
            // Get applicable policies for the route
            const applicablePolicies = this.getApplicablePolicies(route);
            if (applicablePolicies.length === 0) {
                // No specific policies found, use default action
                const defaultAction = this.getDefaultAction(route);
                return this.createEvaluationResult(defaultAction === 'allow', defaultAction === 'allow' ? 'Default allow' : 'No policies found, default deny', [], [], user, route, timestamp, performance.now() - startTime, evalCorrelationId);
            }
            // Sort policies by priority (highest first)
            const sortedPolicies = applicablePolicies.sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0));
            const evaluatedPolicies: string[] = [];
            const failedConditions: AccessCondition[] = [];
            // Evaluate each policy
            for (const policy of sortedPolicies) {
                evaluatedPolicies.push(policy.id);
                if (!policy.active) {
                    continue;
                }
                const evaluation = this.evaluatePolicy(user, policy);
                if (evaluation.granted) {
                    // Access granted by this policy
                    this.logAccessEvent('access_granted', user, route, {
                        granted: true,
                        reason: `Policy ${policy.id} granted access`,
                        evaluatedPolicies,
                        failedConditions,
                        userAttributes: user,
                        timestamp,
                        duration: performance.now() - startTime
                    }, evalCorrelationId);
                    return this.createEvaluationResult(true, `Policy ${policy.id} granted access`, evaluatedPolicies, failedConditions, user, route, timestamp, performance.now() - startTime, evalCorrelationId);
                }
                else {
                    // Collect failed conditions for debugging
                    if (evaluation.failedConditions) {
                        failedConditions.push(...evaluation.failedConditions);
                    }
                }
            }
            // No policies granted access
            const result = this.createEvaluationResult(false, 'No policies granted access', evaluatedPolicies, failedConditions, user, route, timestamp, performance.now() - startTime, evalCorrelationId);
            this.logAccessEvent('access_denied', user, route, result, evalCorrelationId);
            return result;
        }
        catch (error) {
            const errorResult = this.createEvaluationResult(false, `Policy evaluation error: ${error instanceof Error ? error.message : 'Unknown error'}`, [], [], user, route, timestamp, performance.now() - startTime, evalCorrelationId);
            this.logAccessEvent('error', user, route, errorResult, evalCorrelationId);
            return errorResult;
        }
    }
    /**
     * Evaluate a single policy against user attributes
     */
    private static evaluatePolicy(user: UserAttributes, policy: AccessPolicy): {
        granted: boolean;
        failedConditions?: AccessCondition[];
    } {
        const failedConditions: AccessCondition[] = [];
        // Check roles
        if (policy.roles && policy.roles.length > 0) {
            const userRoles = [user.role, ...(user.additionalRoles || [])];
            const isPlatformAdmin = userRoles.some((role) =>
                typeof role === 'string' &&
                ['admin', 'super_admin', 'system_admin', 'policy_admin'].includes(role),
            );
            if (!isPlatformAdmin) {
                const hasRequiredRole = policy.roles.some(role => userRoles.includes(role));
                if (!hasRequiredRole) {
                    return { granted: false };
                }
            }
        }
        // Check permissions
        if (policy.permissions && policy.permissions.length > 0) {
            const userRoles = [user.role, ...(user.additionalRoles || [])];
            const isPlatformAdmin = userRoles.some((role) =>
                typeof role === 'string' &&
                ['admin', 'super_admin', 'system_admin', 'policy_admin'].includes(role),
            );
            if (!isPlatformAdmin) {
                const userPermissions = user.permissions || [];
                const hasRequiredPermissions = policy.permissionMode === 'all'
                    ? policy.permissions.every(permission => userPermissions.includes(permission) || userPermissions.includes('*'))
                    : policy.permissions.some(permission => userPermissions.includes(permission) || userPermissions.includes('*'));
                if (!hasRequiredPermissions) {
                    return { granted: false };
                }
            }
        }
        // Check ABAC conditions
        if (this.config.enableABAC && policy.conditions && policy.conditions.length > 0) {
            for (const condition of policy.conditions) {
                if (!this.evaluateCondition(user, condition)) {
                    failedConditions.push(condition);
                }
            }
            if (failedConditions.length > 0) {
                return { granted: false, failedConditions };
            }
        }
        return { granted: true };
    }
    /**
     * Evaluate a single ABAC condition
     */
    private static evaluateCondition(user: UserAttributes, condition: AccessCondition): boolean {
        // Note: Removed special bypass for a.nava@americanpolicetraining.com
        // All users including test users should follow proper 2FA setup requirements
        const attributeValue = user[condition.attribute];
        switch (condition.operator) {
            case 'equals':
                return attributeValue === condition.value;
            case 'not_equals':
                return attributeValue !== condition.value;
            case 'in':
                return Array.isArray(condition.value) && condition.value.includes(attributeValue);
            case 'not_in':
                return Array.isArray(condition.value) && !condition.value.includes(attributeValue);
            case 'greater_than':
                return typeof attributeValue === 'number' && attributeValue > condition.value;
            case 'less_than':
                return typeof attributeValue === 'number' && attributeValue < condition.value;
            case 'contains':
                return typeof attributeValue === 'string' &&
                    typeof condition.value === 'string' &&
                    attributeValue.includes(condition.value);
            case 'exists':
                return attributeValue !== undefined && attributeValue !== null;
            default:
                if (this.config.debug?.enabled) {
                    logger.warn(`[PolicyEngine] Unknown operator: ${condition.operator}`);
                }
                return false;
        }
    }
    /**
     * Get applicable policies for a route
     */
    private static getApplicablePolicies(route: string): AccessPolicy[] {
        const policies: AccessPolicy[] = [];
        // Check route-specific policies
        for (const routePolicy of this.registry.routes) {
            if (this.matchesRoute(route, routePolicy.route)) {
                // Resolve policy references
                for (const policyRef of routePolicy.policies) {
                    const policy = typeof policyRef === 'string'
                        ? this.registry.global.find(p => p.id === policyRef)
                        : policyRef;
                    if (policy) {
                        policies.push(policy);
                    }
                }
            }
        }
        // Add global policies
        policies.push(...this.registry.global);
        return policies;
    }
    /**
     * Check if a route matches a pattern
     */
    private static matchesRoute(route: string, pattern: string): boolean {
        // Handle wildcard patterns (e.g., '/admin/*')
        if (pattern.endsWith('/*')) {
            const prefix = pattern.slice(0, -2); // Remove '/*'
            return route.startsWith(prefix);
        }
        // Use cached matcher if available
        if (this.routeMatchers.has(pattern)) {
            const matcher = this.routeMatchers.get(pattern);
            return matcher(route);
        }
        // Create new matcher and cache it
        try {
            const matcher = match(pattern, { decode: decodeURIComponent });
            this.routeMatchers.set(pattern, matcher);
            return matcher(route);
        }
        catch (error) {
            if (this.config.debug?.enabled) {
                logger.error(`[PolicyEngine] Invalid route pattern: ${pattern}`, error, {});
            }
            return false;
        }
    }
    /**
     * Get default action for a route
     */
    private static getDefaultAction(route: string): 'allow' | 'deny' {
        // Check if route has specific default action
        for (const routePolicy of this.registry.routes) {
            if (this.matchesRoute(route, routePolicy.route) && routePolicy.defaultAction) {
                return routePolicy.defaultAction;
            }
        }
        // Use global default
        return this.registry.defaults.defaultAction;
    }
    /**
     * Create evaluation result object
     */
    private static createEvaluationResult(granted: boolean, reason: string, evaluatedPolicies: string[], failedConditions: AccessCondition[], user: UserAttributes | null, route: string, timestamp: string, duration: number, correlationId: string): AccessEvaluationResult {
        return {
            granted,
            reason,
            evaluatedPolicies,
            failedConditions,
            userAttributes: user || {} as UserAttributes,
            timestamp,
            duration
        };
    }
    /**
     * Log access events for monitoring and auditing
     */
    private static logAccessEvent(type: AccessMonitoringEvent['type'], user: UserAttributes | null, resource: string, result: AccessEvaluationResult, correlationId: string): void {
        if (!this.config.enableMonitoring) {
            return;
        }
        const event: AccessMonitoringEvent = {
            type,
            user: user || {} as UserAttributes,
            resource,
            result,
            correlationId,
            timestamp: new Date().toISOString(),
            metadata: {
                userAgent: navigator.userAgent,
                url: window.location.href
            }
        };
        // Log to audit service
        AuditService.logAccessEvent(event);
        // Debug logging
        if (this.config.debug?.enabled) {
            logger.debug(`[PolicyEngine] ${type}:`, { "arg0": {
                    user: user?.email || 'anonymous',
                    resource,
                    granted: result.granted,
                    reason: result.reason,
                    correlationId
                } });
        }
    }
    /**
     * Generate a unique correlation ID
     */
    private static generateCorrelationId(): string {
        return `policy_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }
    /**
     * Check if user has specific permission
     */
    static hasPermission(user: UserAttributes | null, permission: Permission): boolean {
        if (!user || !user.permissions) {
            return false;
        }
        return user.permissions.includes(permission) || user.permissions.includes('*');
    }
    /**
     * Check if user has specific role
     */
    static hasRole(user: UserAttributes | null, role: Role): boolean {
        if (!user) {
            return false;
        }
        return user.role === role || (user.additionalRoles || []).includes(role);
    }
    /**
     * Get user's effective permissions (including role-based permissions)
     */
    static getEffectivePermissions(user: UserAttributes | null): Permission[] {
        if (!user) {
            return [];
        }
        const permissions = new Set<Permission>(user.permissions || []);
        // Add role-based permissions
        const userRoles = [user.role, ...(user.additionalRoles || [])];
        for (const role of userRoles) {
            const rolePolicies = this.registry.global.filter(policy => policy.roles?.includes(role) && policy.active);
            for (const policy of rolePolicies) {
                if (policy.permissions) {
                    policy.permissions.forEach(permission => permissions.add(permission));
                }
            }
        }
        return Array.from(permissions);
    }
    /**
     * Validate policy configuration
     */
    static validatePolicies(): {
        valid: boolean;
        errors: string[];
    } {
        const errors: string[] = [];
        // Check for duplicate policy IDs
        const policyIds = new Set<string>();
        for (const policy of this.registry.global) {
            if (policyIds.has(policy.id)) {
                errors.push(`Duplicate policy ID: ${policy.id}`);
            }
            policyIds.add(policy.id);
        }
        // Check for invalid route patterns
        for (const routePolicy of this.registry.routes) {
            try {
                match(routePolicy.route);
            }
            catch (error) {
                errors.push(`Invalid route pattern: ${routePolicy.route}`);
            }
        }
        // Check for orphaned policy references
        for (const routePolicy of this.registry.routes) {
            for (const policyRef of routePolicy.policies) {
                const policyId = typeof policyRef === 'string' ? policyRef : policyRef.id;
                if (!this.registry.global.find(p => p.id === policyId)) {
                    errors.push(`Orphaned policy reference: ${policyId} in route ${routePolicy.route}`);
                }
            }
        }
        return {
            valid: errors.length === 0,
            errors
        };
    }
}
export default PolicyEngine;
